import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Phone, MessageCircle, Mail, MapPin, Clock, Building2 } from "lucide-react";
import { CONTACT, IMAGES } from "../lib/api";
import { Reveal, MaskedLine } from "./motion/Reveal";

export default function Contact() {
  const { t } = useTranslation();

  const cards = [
    { key: "call", icon: Phone, value: CONTACT.phoneDisplay, href: `tel:${CONTACT.phone}`, testid: "contact-phone" },
    { key: "whatsapp", icon: MessageCircle, value: CONTACT.phoneDisplay, href: `https://wa.me/${CONTACT.whatsapp}`, testid: "contact-whatsapp", external: true },
    { key: "email", icon: Mail, value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
  ];

  const openingHours = [
    { k: "weekdays" }, { k: "saturday" }, { k: "sunday" }, { k: "emergency", highlight: true },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="section bg-[#0A1128] relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-10"
        style={{ backgroundImage: `url(${IMAGES.amsterdam})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="mb-12 md:mb-14 max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="accent-bar" />
              <span className="text-[#FF5A1F] uppercase font-bold tracking-widest text-xs">
                {t("contact.eyebrow")}
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white mb-5 leading-[1.02]">
            <span className="block overflow-hidden"><MaskedLine text={t("contact.title_1")} wordDelay={0.06} /></span>
            <span className="block overflow-hidden text-[#FF5A1F]"><MaskedLine text={t("contact.title_2")} wordDelay={0.06} delay={0.15} /></span>
          </h2>
          <Reveal delay={0.15}>
            <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-2xl">
              {t("contact.subtitle", { region: CONTACT.region })}
            </p>
          </Reveal>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px 0px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10 md:mb-12"
        >
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <motion.a
                key={c.key}
                href={c.href}
                data-testid={c.testid}
                target={c.external ? "_blank" : undefined}
                rel={c.external ? "noopener noreferrer" : undefined}
                variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } }}
                whileHover={{ y: -4 }}
                className="group grid-card-dark p-6 md:p-7 flex items-start gap-4 hover:border-[#FF5A1F]"
              >
                <div className="w-12 h-12 bg-[#FF5A1F] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <Icon className="w-6 h-6 text-white" strokeWidth={2.4} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-white/60 font-bold mb-1">{t(`contact.labels.${c.key}`)}</div>
                  <div className="font-display text-lg md:text-xl text-white group-hover:text-[#FF5A1F] transition-colors truncate">{c.value}</div>
                </div>
              </motion.a>
            );
          })}
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6 md:gap-8">
          <div className="lg:col-span-7 relative border border-white/10 overflow-hidden aspect-[16/10] lg:aspect-auto min-h-[320px]">
            <iframe
              title="Ataiwo Amsterdam"
              src="https://www.google.com/maps?q=Amsterdam,+Noord-Holland,+Netherlands&z=10&output=embed"
              className="absolute inset-0 w-full h-full grayscale contrast-125 opacity-90"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map"
            />
            <div className="absolute top-4 left-4 bg-[#0A1128] text-white px-4 py-2 border-l-4 border-[#FF5A1F] flex items-center gap-2 pointer-events-none">
              <MapPin className="w-4 h-4 text-[#FF5A1F]" />
              <span className="text-xs uppercase font-bold tracking-wider">{t("contact.map_label")}</span>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-4">
            <div className="bg-white/[0.04] border border-white/10 p-6 md:p-7">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-[#FF5A1F]" />
                <h3 className="font-display text-xl md:text-2xl text-white">{t("contact.hours_title")}</h3>
              </div>
              <ul className="divide-y divide-white/10">
                {openingHours.map((o, i) => (
                  <li key={i} className="flex justify-between items-center py-2.5 text-sm">
                    <span className={`${o.highlight ? "text-[#FF5A1F] font-bold" : "text-white/70"}`}>
                      {t(`contact.hours.${o.k}`)}
                    </span>
                    <span className={`font-semibold ${o.highlight ? "text-[#FF5A1F]" : "text-white"}`}>
                      {t(`contact.hours.${o.k}_time`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white/[0.04] border border-white/10 p-6 md:p-7">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-[#FF5A1F]" />
                <h3 className="font-display text-xl md:text-2xl text-white">{CONTACT.companyName}</h3>
              </div>
              <ul className="text-white/70 space-y-2 text-sm">
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FF5A1F]" /> {CONTACT.region}</li>
                <li>KvK: <span className="text-white font-semibold">{CONTACT.kvk}</span></li>
                <li>Website: <a href={`https://${CONTACT.website}`} className="text-white hover:text-[#FF5A1F]">{CONTACT.website}</a></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
